import { diagonal3D, distance3D } from '@paragon/utilities';

import Direction3D from './Direction3D';
import Line3D from './Line3D';
import Plane3D from './Plane3D';
import Point2D from './Point2D';
import { Rotation } from './rotation';
import { convertDegreesToRadians, distanceEquals, straightAngle } from './units';
import Vector3D from './Vector3D';

export interface Point3DInterface {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

// A vector in R^3, modeling a point or a displacement in a three-dimensional Cartesian coordinate system.
class Point3D {
  static readonly origin = new Point3D(0, 0, 0);

  readonly x: number;
  readonly y: number;
  readonly z: number;

  constructor(x: number, y: number, z: number) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  static fromObject({ x, y, z }: Point3DInterface) {
    return new Point3D(x, y, z);
  }

  get coordinates() {
    return [this.x, this.y, this.z];
  }

  get magnitude() {
    return diagonal3D(this.x, this.y, this.z);
  }

  equals({ x, y, z }: Point3D) {
    return distanceEquals(distance3D(this.x, this.y, this.z, x, y, z), 0);
  }

  negative() {
    return this.scaleBy(-1);
  }

  add(other: Point3D) {
    return new Point3D(this.x + other.x, this.y + other.y, this.z + other.z);
  }

  addX(dx: number) {
    return new Point3D(this.x + dx, this.y, this.z);
  }

  addY(dy: number) {
    return new Point3D(this.x, this.y + dy, this.z);
  }

  addZ(dz: number) {
    return new Point3D(this.x, this.y, this.z + dz);
  }

  subtract(other: Point3D) {
    return this.add(other.negative());
  }

  distanceToPoint(other: Point3D) {
    // TODO Use Vector3D when that exists.
    return other.subtract(this).magnitude;
  }

  distanceToLine(line: Line3D) {
    const projected = this.projectOntoLine(line);
    return this.distanceToPoint(projected);
  }

  projectOntoLine(line: Line3D) {
    const pointOnLine = line.pointNearestOrigin;
    const vector = this.subtract(pointOnLine);
    return pointOnLine.add(vector.projectOntoDirection(line.direction));
  }

  /** Projects a point onto a plane from the normal direction. */
  projectOntoPlane(plane: Plane3D): Point3D {
    const distance = plane.signedDistanceTo(this);
    const vector = plane.halfSpace.direction.toVector(-distance);
    return this.translate(vector);
  }

  projectedSignedDistanceAlong(line: Line3D) {
    return this.subtract(line.pointNearestOrigin).dotProduct(line.direction.toPoint(1));
  }

  /** Rotates in 2D by the given angle around the given point. */
  rotate2D(angle: number, center: Point2D = Point2D.origin) {
    const center3D = center.to3D();
    const angleInRadians = convertDegreesToRadians(angle);
    const cosTheta = Math.cos(angleInRadians);
    const sinTheta = Math.sin(angleInRadians);

    const translated = this.subtract(center3D);
    return new Point3D(
      translated.x * cosTheta - translated.y * sinTheta,
      translated.x * sinTheta + translated.y * cosTheta,
      this.z,
    ).add(center3D);
  }

  /** Rotates a point about an axis with the given angle (returns a new point in that location) */
  rotate3D(rotationToApply: Rotation): Point3D {
    return rotationToApply.mapPoint(this);
  }

  scaleBy(scaleFactor: number) {
    return new Point3D(this.x * scaleFactor, this.y * scaleFactor, this.z * scaleFactor);
  }
  scaleToMagnitude(newMagnitude: number) {
    return this.scaleBy(newMagnitude / this.magnitude);
  }

  divideScalar(scalar: number) {
    return new Point3D(this.x / scalar, this.y / scalar, this.z / scalar);
  }

  dotProduct(other: Point3D) {
    return this.x * other.x + this.y * other.y + this.z * other.z;
  }
  crossProduct(other: Point3D) {
    return new Point3D(
      this.y * other.z - this.z * other.y,
      this.z * other.x - this.x * other.z,
      this.x * other.y - this.y * other.x,
    );
  }

  /**
   * Compute the component of this vector parallel to direction.
   */
  projectOntoDirection(direction: Direction3D) {
    const directionVector = direction.toPoint();
    return directionVector.scaleBy(this.dotProduct(directionVector));
  }

  /**
   * Compute the component of this vector perpendicular to direction.
   */
  rejectDirection(direction: Direction3D) {
    return this.subtract(this.projectOntoDirection(direction));
  }

  projectTo2D(): Point2D {
    return Point2D.fromObject(this);
  }

  translate(vector: Vector3D) {
    return new Point3D(this.x + vector.x, this.y + vector.y, this.z + vector.z);
  }

  mirrorAcross(passedAxisLine: Line3D) {
    return this.rotate3D(Rotation.fromAxisAndAngle(passedAxisLine, straightAngle));
  }

  toObject() {
    return { x: this.x, y: this.y, z: this.z };
  }

  toArray(): [number, number, number] {
    return [this.x, this.y, this.z];
  }

  toVector() {
    return new Vector3D(this.x, this.y, this.z);
  }
}

export default Point3D;
